import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import AfWhitelistForm from 'routes/AfWhitelist/components/AfWhitelistForm';
import { Ref } from 'routes/AfWhitelist/components/AfWhitelistForm/AfWhitelistForm';
import { FormTemplateOption, FormTemplateSection } from 'data/template/template.types';
import { RootState } from 'data/root.reducers';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as distributorActions from 'data/distributor/distributor.actions';
import { Distributor, DistributorData } from 'data/distributor/distributor.types';
import * as afWhitelistActions from 'data/afWhitelist/afWhitelist.actions';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AdaptedAfWhitelist, AfWhitelist } from 'data/afWhitelist/afWhitelist.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newAfWhitelist } from 'routes/AfWhitelist/AfWhitelist.type';
import { afWhitelistTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  setOptionForTemplateSectionField,
  getAfTagsToProtocolMapKey,
  getInstanceOptions,
  getAfTypeOptions,
  getAfProtocolOptions,
} from '../../utils/afWhitelistTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/afWhitelistTemplateDataToSubmit';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  afWhitelistTemplateDef: adaptTemplateVisibilityMode(
    afWhitelistTemplate,
    'create',
  ),
  userId: state.authentication.userId,
  distributors: distributorSelectors.getDistributors(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  enabledAccessFunction: supportedFeaturesSelectors.getEnabledAccessFunction(state),
});

const mapDispatch = {
  getDistributors: distributorActions.getDistributors,
  createAfWhitelist: afWhitelistActions.createAfWhitelist,
  goToAfWhitelistList: afWhitelistActions.goToAfWhitelistList,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const AfWhitelistCreate = ({
  history,
  userId,
  goToAfWhitelistList,
  createAfWhitelist,
  userName,
  isAuditEnabled,
  afWhitelistTemplateDef,
  distributors,
  availableAccessFunction,
  enabledAccessFunction,
  getDistributors,
  deliveryFunctionId
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<AdaptedAfWhitelist>(
    cloneDeep(newAfWhitelist),
  );
  const [template, setTemplate] = useState(afWhitelistTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getDistributors(deliveryFunctionId);
  }, [deliveryFunctionId, getDistributors]);

  useEffect(() => {
    if (template == null || formData == null) {
      return;
    }
    const adaptedTemplate = adaptTemplateOptions(template, distributors, enabledAccessFunction, availableAccessFunction);
    setTemplate(adaptedTemplate);
  }, [distributors])

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
      if (template == null || formData == null) {
        return;
      }

      const clonedFormData = cloneDeep(formData as AdaptedAfWhitelist);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      if (fieldName === 'AFTYPE') {
        // Update af protocol options
        const protocolOptions = getAfProtocolOptions(distributors, value);
        setOptionForTemplateSectionField(clonedTemplate.main as FormTemplateSection, protocolOptions, 'AFPROTOCOL');
        (clonedTemplate.main as FormTemplateSection).fields.AFPROTOCOL.initial = 'ANY';
        clonedFormData.AFPROTOCOL = 'ANY';
      }

      const newData = {
        ...clonedFormData,
        [fieldName]: value,
      };

      setFormData(newData);
      setTemplate(clonedTemplate);
  };

  const getCreateAfWhitelistActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const afWhitelistFormData = formRef.current.getValues();
    const afWhitelistSubmitData : AfWhitelist = {
      id: '',
      AFID: afWhitelistFormData.AFID,
      AFTYPE: afWhitelistFormData.AFTYPE,
      AFPROTOCOL: afWhitelistFormData.AFPROTOCOL,
      IPADDR: afWhitelistFormData.IPADDR,
      GRAYITEM: afWhitelistFormData.GRAYITEM ? 'Y' : 'N'
    }

    const payload = {
      data: afWhitelistSubmitData,
      auditDetails: getAuditDetailsToSubmit(
        afWhitelistFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateAfWhitelistActionPayload();
    if (payload) {
      createAfWhitelist(payload, history);
    }
  };

  const handleCancel = () => goToAfWhitelistList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <AfWhitelistForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="AfWhitelistCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('afWhitelist.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
 };

export default withRouter(connector(AfWhitelistCreate));
